import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, clearCredentials } from '@/redux/slices/authSlice';
import { clearPlan } from '@/redux/slices/planSlice';
import { authService } from '@/services/auth.service';
import { runBootstrapRefreshIfNeeded, scheduleAccessTokenRefresh } from '@/services/api';
import { autoInitPosShiftStore } from '@/utils/posShift';
import type { AppDispatch, RootState } from '@/redux';

const ACCESS_TOKEN_KEY = 'erp_access_token';
const REFRESH_TOKEN_KEY = 'erp_refresh_token';

/**
 * App boot hook — đảm bảo Redux auth state đồng bộ với token trong localStorage.
 *
 * Chạy 1 lần khi App mount:
 *  - Có token trong localStorage NHƯNG Redux user empty (vd: localStorage manual,
 *    different storage, persist rehydrate fail) → call `GET /auth/me` để hydrate
 *    full context (ADR-012 §Frontend contract). Token invalid → clear + redirect
 *    login (interceptor sẽ tự handle 401).
 *  - Không token → no-op.
   *  - Có token + có user → vẫn gọi `/auth/me`; permission runtime không được lấy
   *    từ persisted role/user cũ.
 *
 * Trả `bootstrapping=true` trong khi chờ /auth/me để App hiện spinner thay vì
 * flash login → home.
 *
 * `hasHydratedRef` (boolean) thay cho so khớp CHÍNH XÁC giá trị
 * access token. Bản cũ dùng `hydratedTokenRef.current === tokenKey`: mỗi lần
 * `scheduleAccessTokenRefresh` (api.ts) tự xoay access token NGẦM (phiên vẫn
 * còn nguyên, user không hề re-login) thì `accessToken` đổi giá trị →
 * `tokenKey` không khớp ref cũ → hook tưởng đây là phiên MỚI chưa hydrate →
 * `bootstrapping=true` → `App.tsx` unmount hẳn `<Routes>` để hiện spinner →
 * gọi thừa `/auth/me` → remount lại toàn bộ cây route. QueryClient có
 * `staleTime: 0` (main.tsx) nên remount = MỌI query đang mount (cả loạt
 * widget /dashboard) refetch lại đồng loạt — trông như "tự động reload định
 * kỳ" dù không hề có `refetchInterval`/`setInterval` nào. Chu kỳ lặp thực ra
 * ăn theo hạn access token (PROACTIVE_REFRESH_MARGIN_SEC trước khi hết hạn).
 *
 * Chỉ có 2 trường hợp thật sự cần hydrate lại qua `/auth/me`: (1) mount lần
 * đầu của tab (`hasHydratedRef.current` chưa từng bật), (2) sau logout rồi
 * login lại (nhánh `!hasToken` phía dưới reset `hasHydratedRef` về false).
 * Token đổi vì rotate ngầm hoặc đổi chi nhánh (`exchangeStoreToken` trong
 * `useStoreSelection`) đều không cần gọi lại — chỗ dispatch `setCredentials`
 * tương ứng đã tự cung cấp `user` mới rồi.
 */
export function useAuthBootstrap(): { bootstrapping: boolean } {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((s: RootState) => s.auth.user);
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const [bootstrapping, setBootstrapping] = useState<boolean>(() => {
    // Khởi tạo true nếu có token mà user chưa có (cần hydrate)
    if (typeof window === 'undefined') return false;
    const tokenInStorage = localStorage.getItem(ACCESS_TOKEN_KEY);
    return !!tokenInStorage;
  });
  const hasHydratedRef = useRef(false);
  // `user` chỉ cần đọc bên trong effect (nhánh short-circuit), KHÔNG được đưa
  // vào dependency array: effect này tự `dispatch(setUser(u))` bên trong nó,
  // đưa `user` vào deps tạo vòng effect-tự-kích-hoạt-lại-chính-nó mỗi lần
  // hydrate xong (kể cả khi vô hại nhờ `hasHydratedRef`, vẫn là một
  // anti-pattern nên bỏ hẳn thay vì dựa vào guard để che).
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    let cancelled = false;
    const tokenInStorage =
      typeof window !== 'undefined' ? localStorage.getItem(ACCESS_TOKEN_KEY) : null;
    const hasToken = !!(accessToken || tokenInStorage);

    if (hasToken) {
      scheduleAccessTokenRefresh();
    }

    if (!hasToken) {
      hasHydratedRef.current = false;
      setBootstrapping(false);
      return;
    }

    if (hasHydratedRef.current) {
      // Phiên đã hydrate rồi — accessToken đổi giá trị ở đây chỉ do rotate
      // ngầm hoặc đổi chi nhánh, không phải phiên mới cần re-verify qua
      // /auth/me.
      // Rehydrate path: Redux Persist restore `auth` từ localStorage qua tab/reload,
      // nhưng `pos.shiftStoreId` ở sessionStorage KHÔNG cross-tab. Phải trigger
      // autoInit khi user đã có để owner có deliveryStoreId / single-store user
      // không vướng gate "chưa được gán chi nhánh" tại POSPage (EG-369).
      // Idempotent — autoInit no-op nếu sessionStorage đã set.
      if (userRef.current) autoInitPosShiftStore(userRef.current);
      setBootstrapping(false);
      return;
    }

    setBootstrapping(true);
    runBootstrapRefreshIfNeeded()
      .catch(() => {
        // Terminal refresh failures already cleared tokens and redirected.
        return null;
      })
      .then((result) => {
        if (result === null) return null;
        return authService.me();
      })
      .then((u) => {
        if (!u) return;
        if (cancelled) return;
        hasHydratedRef.current = true;
        dispatch(setUser(u));
        autoInitPosShiftStore(u);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const status = (error as { response?: { status?: unknown } })?.response?.status;
        if (status !== 401) return;
        // Only a definitive authentication failure may destroy a persisted session.
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        dispatch(clearCredentials());
        dispatch(clearPlan());
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `user` cố ý không
    // nằm trong deps, xem comment `userRef` phía trên.
  }, [accessToken, dispatch]);

  return { bootstrapping };
}
